import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Box } from "@mui/system";
import { CircularProgress } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { Api } from "../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../hooks";
import {
  AutomationVendorTaskDto,
  AutomationVendorTaskStatus,
  AutomationVendorTaskType,
  VendorOutcomeValue,
} from "../../../../common/service/server/dto/AutomationVendorTaskDto";
import { AlertCard, DateDisplay, Pill, Table, ToolTip, VendorLogo } from "@truevault/aquilo";
import { TableDensity } from "@truevault/aquilo/src/components/table/Table";
import { RouteHelpers, Routes } from "../../../root/routes";
import { ClockIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

const EmDash = () => <span className="text-gray-400">—</span>;

export const AutomationHistoryTab = () => {
  const [org] = usePrimaryOrganization();
  const organizationId = org?.id;

  const [data, setData] = React.useState<AutomationVendorTaskDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const pageSize = 20;
  const [totalPages, setTotalPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [copiedTaskId, setCopiedTaskId] = React.useState<string | null>(null);
  const [failedOnly, setFailedOnly] = React.useState(false);

  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async (page: number, filterFailedOnly: boolean) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);

      if (organizationId) {
        try {
          const response = await Api.organizationVendor.getAutomationHistory(
            organizationId,
            page - 1,
            pageSize,
            filterFailedOnly,
          );

          if (fetchId === fetchIdRef.current) {
            setData(response.content);
            setTotalPages(response.totalPages);
          }
        } catch (e) {
          console.error(e);
        } finally {
          if (fetchId === fetchIdRef.current) {
            setLoading(false);
          }
        }
      }
    },
    [organizationId],
  );

  useEffect(() => {
    fetchData(currentPage, failedOnly);
  }, [fetchData, currentPage, failedOnly]);

  const copyToClipboard = (text: string, taskId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedTaskId(taskId);
    setTimeout(() => setCopiedTaskId(null), 2000);
  };

  const truncateMessage = (message: string, maxLength: number = 100) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  const getReconnectUrl = (vendorIntegrationName?: string): string => {
    const base = `${Routes.privacyCenter.integrations}?tab=connections`;
    // Don't add reconnect param for Shopify (OAuth) or if no vendor name
    if (!vendorIntegrationName || vendorIntegrationName === "shopify") {
      return base;
    }
    return `${base}&reconnect=${encodeURIComponent(vendorIntegrationName)}`;
  };

  const getOutcomeDisplay = (
    status: AutomationVendorTaskStatus,
    outcome?: VendorOutcomeValue,
    responseBody?: string,
    taskId?: string,
    vendorIntegrationName?: string,
  ): React.ReactNode => {
    const isCopied = taskId && copiedTaskId === taskId;
    const tooltipText = isCopied
      ? `Copied: ${truncateMessage(responseBody || "")}`
      : `Click to copy: ${truncateMessage(responseBody || "")}`;

    const failedPillWithInfo = (
      <span className="flex items-center gap-1">
        <Pill variant="error" noMargin>
          Failed
        </Pill>
        {responseBody && taskId && (
          <ToolTip text={tooltipText}>
            <InformationCircleIcon
              className="w-4 h-4 text-gray-500 cursor-pointer"
              onClick={() => copyToClipboard(responseBody, taskId)}
            />
          </ToolTip>
        )}
        <Link
          to={getReconnectUrl(vendorIntegrationName)}
          className="text-blue-600 hover:underline text-sm"
        >
          Reconnect?
        </Link>
      </span>
    );

    // If SENT, show hourglass icon
    if (status === AutomationVendorTaskStatus.SENT) {
      return <ClockIcon className="w-5 h-5 text-amber-400" />;
    }

    // If status is FAILED, show failed pill with info icon
    if (status === AutomationVendorTaskStatus.FAILED) {
      return failedPillWithInfo;
    }

    // Otherwise show outcome pill
    switch (outcome) {
      case VendorOutcomeValue.AUTOMATION_CONFIRMED:
        return (
          <Pill variant="completed" noMargin>
            Confirmed
          </Pill>
        );
      case VendorOutcomeValue.AUTOMATION_FAILED:
        return failedPillWithInfo;
      case VendorOutcomeValue.AUTOMATION_NO_DATA:
        return (
          <Pill variant="completed" noMargin>
            Consumer Not Found
          </Pill>
        );
      case VendorOutcomeValue.AUTOMATION_REQUESTED:
        return (
          <Pill variant="info" noMargin>
            Requested
          </Pill>
        );
      default:
        return <EmDash />;
    }
  };

  const getTypeLabel = (type?: AutomationVendorTaskType): React.ReactNode => {
    switch (type) {
      case AutomationVendorTaskType.OPT_OUT:
        return "Opt Out";
      case AutomationVendorTaskType.DELETE:
        return "Delete";
      case AutomationVendorTaskType.LOOKUP:
        return "Lookup";
      case AutomationVendorTaskType.ACCESS:
        return "Access";
      default:
        return <EmDash />;
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "resolvedAt",
        header: "Date",
        render: (row: AutomationVendorTaskDto) =>
          row.resolvedAt ? <DateDisplay date={row.resolvedAt} hoverForRelative /> : <EmDash />,
      },
      {
        key: "dataRecipientIntegrationName",
        header: "Vendor",
        render: (row: AutomationVendorTaskDto) => (
          <ToolTip text={row.dataRecipientName || row.dataRecipientIntegrationName || "Unknown"}>
            <VendorLogo
              vendorLogoUrl={row.dataRecipientLogoFilename}
              vendorName={row.dataRecipientIntegrationName}
              size="s"
            />
          </ToolTip>
        ),
      },
      {
        key: "requestType",
        header: "Type",
        render: (row: AutomationVendorTaskDto) => getTypeLabel(row.requestType),
      },
      {
        key: "vendorOutcome",
        header: "Outcome",
        render: (row: AutomationVendorTaskDto) =>
          getOutcomeDisplay(
            row.status,
            row.vendorOutcome,
            row.responseBody,
            row.id,
            row.dataRecipientIntegrationName,
          ),
      },
      {
        key: "dataRequestPublicId",
        header: "Request",
        render: (row: AutomationVendorTaskDto) => {
          const {
            dataRequestPublicId,
            consumerName,
            dataRequestState,
            dataRequestClosedAt,
            isBatched,
            batchRepresentativeRequestPublicId,
          } = row;
          if (!dataRequestPublicId) {
            return <EmDash />;
          }

          const isClosed = dataRequestState === "CLOSED";
          const displayName = consumerName || dataRequestPublicId;

          // Status dot with tooltip
          const statusDot = isClosed ? (
            <ToolTip
              text={
                dataRequestClosedAt
                  ? `Request Closed on ${new Date(dataRequestClosedAt).toLocaleDateString()}`
                  : "Request Closed"
              }
            >
              <span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-2" />
            </ToolTip>
          ) : (
            <ToolTip text="Request is Open">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-2" />
            </ToolTip>
          );

          // If batched, show name (not a link) with "(Batched)" link
          if (isBatched && batchRepresentativeRequestPublicId) {
            return (
              <span className="flex items-center">
                {statusDot}
                <span>{displayName}</span>
                <span className="text-gray-500 ml-1">(</span>
                <Link
                  to={RouteHelpers.requests.detail(batchRepresentativeRequestPublicId)}
                  className="text-blue-600 hover:underline"
                >
                  Batched
                </Link>
                <span className="text-gray-500">)</span>
              </span>
            );
          }

          // Regular request - name is a link
          return (
            <span className="flex items-center">
              {statusDot}
              <Link
                to={RouteHelpers.requests.detail(dataRequestPublicId)}
                className="text-blue-600 hover:underline"
              >
                {displayName}
              </Link>
            </span>
          );
        },
      },
    ],
    [copiedTaskId],
  );

  if (!organizationId) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="tw-boundary">
      <div className="mb-4">
        <AlertCard variant="info">
          <p className="text-sm text-gray-700">
            This is a historical view of automated vendor requests. If any show a Failed status, we
            recommend reconnecting the integration in the{" "}
            <Link
              to={`${Routes.privacyCenter.integrations}?tab=connections`}
              className="text-blue-600 hover:underline font-medium"
            >
              Connections
            </Link>{" "}
            tab, then returning to the request to retry the automation.
          </p>
        </AlertCard>
      </div>
      <div className="mb-4 flex items-center">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={(e) => {
              setFailedOnly(e.target.checked);
              setCurrentPage(1);
            }}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Show only failed (last 30 days)</span>
        </label>
      </div>
      <Table
        data={data}
        columns={columns}
        keyExtractor={(row: AutomationVendorTaskDto) => row.id}
        tableDensity={TableDensity.COMPACT}
      />
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, newPage) => setCurrentPage(newPage)}
            color="primary"
          />
        </Box>
      )}
    </div>
  );
};
